import type {
  ConcurrencySequenceSpec,
  ResolvedFigure,
  SequenceEvent,
  SequenceParticipant,
  ShapeRole,
} from '../model.js';
import { box, finishFigure, point, roundGeometry, SceneBuilder, validateViewport } from '../scene-builder.js';

const TOP = 42;
const HEADER_HEIGHT = 52;
const TRACE_TOP = 126;
const BOTTOM_MARGIN = 34;

/**
 * Compiles a logical concurrency trace into a sequence view.
 *
 * `visibleThrough` is a reveal count: 0 shows the participants only, while
 * `events.length` shows the complete trace. Event slots are always calculated
 * from the complete trace, so progressively revealed events do not jump.
 */
export function compileConcurrencySequence(spec: ConcurrencySequenceSpec): ResolvedFigure {
  validateConcurrencySequence(spec);

  const builder = new SceneBuilder();
  const { width, height } = spec.viewport;
  const revealCount = spec.visibleThrough ?? spec.events.length;
  const visibleEvents = spec.events.slice(0, revealCount);
  // Edge lanes reserve half a participant card, so even their touch targets
  // remain inside the SVG at the 320 px floor.
  const sideMargin = width <= 320 ? 36 : width <= 390 ? 40 : 60;
  const traceWidth = width - sideMargin * 2;
  const laneStep = spec.participants.length === 1 ? 0 : traceWidth / (spec.participants.length - 1);
  const laneXs = new Map(spec.participants.map((participant, index) => [
    participant.id,
    roundGeometry(sideMargin + laneStep * index),
  ]));
  const eventStep = (height - TRACE_TOP - BOTTOM_MARGIN) / Math.max(1, spec.events.length);
  const headerWidth = clamp(laneStep * 0.74, width <= 390 ? 54 : 72, width <= 390 ? 82 : 112);
  const warnings: string[] = [];

  builder.text('sequence.eyebrow', spec.id, 'annotation', point(sideMargin, 24), 'CONCURRENCY TRACE · LOGICAL TIME ↓', {
    size: 10,
    mono: true,
    weight: 700,
    uppercase: true,
  });

  spec.participants.forEach((participant, index) => {
    const laneX = laneXs.get(participant.id)!;
    const headerBounds = box(laneX - headerWidth / 2, TOP, headerWidth, HEADER_HEIGHT);
    const labelMax = Math.max(6, Math.floor((headerWidth - 12) / 6.2));
    const displayLabel = compactParticipantLabel(participant.label, labelMax);

    builder.rect(`participant.${participant.id}.header`, `participant.${participant.id}`, participantRole(participant), headerBounds, {
      radius: 5,
      label: participant.label,
      interactive: true,
    });
    builder.text(`participant.${participant.id}.label`, `participant.${participant.id}`, 'structure', point(laneX, TOP + 23), displayLabel, {
      size: width <= 390 ? 9 : 10.5,
      mono: true,
      weight: 700,
      align: 'middle',
    });
    builder.text(`participant.${participant.id}.role`, `participant.${participant.id}`, 'annotation', point(laneX, TOP + 41), participant.role.toUpperCase(), {
      size: 7.5,
      mono: true,
      weight: 700,
      align: 'middle',
      uppercase: true,
    });
    builder.line(`participant.${participant.id}.lane`, `participant.${participant.id}`, 'lane', point(laneX, TOP + HEADER_HEIGHT), point(laneX, height - BOTTOM_MARGIN + 8), {
      dash: index % 2 === 0 ? '4 5' : '2 5',
    });
    builder.anchor(`participant.${participant.id}.top`, `participant.${participant.id}`, point(laneX, TOP), 'up');
    builder.anchor(`participant.${participant.id}.lane-start`, `participant.${participant.id}`, point(laneX, TOP + HEADER_HEIGHT), 'down');
    builder.anchor(`participant.${participant.id}.lane-end`, `participant.${participant.id}`, point(laneX, height - BOTTOM_MARGIN + 8), 'down');
    builder.hit(`participant.${participant.id}`, headerBounds);

    if (displayLabel !== participant.label) warnings.push(`Participant label compacted: ${participant.id}`);
  });

  visibleEvents.forEach((event, index) => {
    const y = roundGeometry(TRACE_TOP + eventStep * (index + 0.56));
    drawEvent(builder, spec, event, index, y, laneXs, eventStep, warnings);
  });

  const focusEvent = spec.focusEventId ? spec.events.find((event) => event.id === spec.focusEventId) : undefined;
  const active = focusEvent
    ? [`event.${focusEvent.id}`, `participant.${focusEvent.from}`, `participant.${focusEvent.to}`]
    : revealCount > 0
      ? [`event.${spec.events[revealCount - 1]!.id}`]
      : spec.participants.map((participant) => `participant.${participant.id}`);
  active.push(...(spec.emphasis ?? []));

  if (eventStep < 38) warnings.push('Event rows are approaching the sequence-view density floor.');
  if (laneStep < 72) warnings.push('Participant lanes are using the narrow-label policy.');

  return finishFigure(spec, builder, {
    title: 'Concurrency is a sequence of causal hand-offs',
    subtitle: 'Stable lanes preserve identity while progressive reveal exposes ordering, blocking, and return paths.',
    state: `revealed:${revealCount}/${spec.events.length}${focusEvent ? ` · focus:${focusEvent.id}` : ''}`,
    active,
    warnings: unique(warnings),
  });
}

export function validateConcurrencySequence(spec: ConcurrencySequenceSpec): void {
  validateViewport(spec.viewport.width, spec.viewport.height);
  if (spec.participants.length < 2) throw new Error('concurrency-sequence: at least two participants are required');
  if (spec.participants.length > 5) throw new Error('concurrency-sequence: at most five participants are supported');
  if (!spec.events.length) throw new Error('concurrency-sequence: at least one event is required');

  assertUnique(spec.participants.map((participant) => participant.id), 'participant');
  assertUnique(spec.events.map((event) => event.id), 'event');
  const participantIds = new Set(spec.participants.map((participant) => participant.id));

  for (const participant of spec.participants) {
    if (!participant.id.trim() || !participant.label.trim()) throw new Error('concurrency-sequence: participant IDs and labels must not be blank');
  }
  for (const event of spec.events) {
    if (!event.id.trim() || !event.label.trim()) throw new Error('concurrency-sequence: event IDs and labels must not be blank');
    if (!participantIds.has(event.from) || !participantIds.has(event.to)) {
      throw new Error(`concurrency-sequence: event ${event.id} references an unknown participant`);
    }
    if (event.from === event.to && event.kind !== 'wait') {
      throw new Error(`concurrency-sequence: only wait events may target their source (${event.id})`);
    }
  }

  const revealCount = spec.visibleThrough ?? spec.events.length;
  if (!Number.isInteger(revealCount) || revealCount < 0 || revealCount > spec.events.length) {
    throw new Error('concurrency-sequence: visibleThrough must be an integer from 0 through events.length');
  }
  if (spec.focusEventId) {
    const focusIndex = spec.events.findIndex((event) => event.id === spec.focusEventId);
    if (focusIndex < 0) throw new Error(`concurrency-sequence: focus event ${spec.focusEventId} does not exist`);
    if (focusIndex >= revealCount) throw new Error(`concurrency-sequence: focus event ${spec.focusEventId} is not yet visible`);
  }
}

function drawEvent(
  builder: SceneBuilder,
  spec: ConcurrencySequenceSpec,
  event: SequenceEvent,
  index: number,
  y: number,
  laneXs: Map<string, number>,
  eventStep: number,
  warnings: string[],
): void {
  const { width } = spec.viewport;
  const sourceX = laneXs.get(event.from)!;
  const targetX = laneXs.get(event.to)!;
  const semanticId = `event.${event.id}`;
  const eventRole = roleForEvent(event.kind);
  const ordinalX = width <= 390 ? 9 : 17;
  const ordinalSize = width <= 390 ? 8 : 9;

  builder.text(`${semanticId}.ordinal`, semanticId, 'annotation', point(ordinalX, y + 3), String(index + 1).padStart(2, '0'), {
    size: ordinalSize,
    mono: true,
    weight: 700,
    align: 'middle',
  });

  let eventBounds;
  let labelCenterX;
  let labelRoom;

  if (sourceX === targetX) {
    const loopWidth = clamp(width * 0.13, 34, 58);
    const loopHeight = Math.min(25, eventStep * 0.58);
    const direction = sourceX + loopWidth + 8 <= width ? 1 : -1;
    const edgeX = sourceX + loopWidth * direction;
    builder.path(
      `${semanticId}.path`,
      semanticId,
      eventRole,
      `M ${n(sourceX)} ${n(y - loopHeight / 2)} C ${n(edgeX)} ${n(y - loopHeight / 2)}, ${n(edgeX)} ${n(y + loopHeight / 2)}, ${n(sourceX)} ${n(y + loopHeight / 2)}`,
      { markerEnd: true, dash: '4 3', label: event.label },
    );
    labelCenterX = sourceX + (loopWidth / 2 + 7) * direction;
    labelRoom = Math.max(48, loopWidth + 22);
    eventBounds = box(Math.min(sourceX, edgeX) - 8, y - loopHeight / 2 - 14, Math.abs(edgeX - sourceX) + 16, loopHeight + 28);
  } else {
    const direction = targetX > sourceX ? 1 : -1;
    const startX = sourceX + 8 * direction;
    const endX = targetX - 10 * direction;
    const dash = event.kind === 'return' ? '7 5' : event.kind === 'wait' ? '3 4' : undefined;
    builder.line(
      `${semanticId}.path`,
      semanticId,
      eventRole,
      point(startX, y),
      point(endX, y),
      { markerEnd: true, ...(dash ? { dash } : {}) },
    );
    labelCenterX = (sourceX + targetX) / 2;
    labelRoom = Math.max(52, Math.abs(targetX - sourceX) - 18);
    eventBounds = box(Math.min(sourceX, targetX) - 4, y - 19, Math.abs(targetX - sourceX) + 8, 38);
  }

  const labelMax = clamp(Math.floor(labelRoom / (width <= 390 ? 5.4 : 6.1)), 7, width <= 390 ? 20 : 30);
  const displayLabel = compactLabel(event.label, labelMax);
  const labelWidth = Math.min(labelRoom, Math.max(42, displayLabel.length * (width <= 390 ? 5.1 : 5.8) + 14));
  const labelY = y - 21;
  builder.rect(`${semanticId}.label-plate`, semanticId, event.kind === 'error' ? 'negative' : 'surface', box(labelCenterX - labelWidth / 2, labelY - 10, labelWidth, 16), {
    radius: 8,
    label: event.label,
  });
  builder.text(`${semanticId}.label`, semanticId, 'structure', point(labelCenterX, labelY + 2), displayLabel, {
    size: width <= 390 ? 8.5 : 9.5,
    mono: true,
    weight: 700,
    align: 'middle',
  });

  const targetMarkerWidth = width <= 390 ? 5 : 7;
  builder.rect(`${semanticId}.target`, semanticId, eventRole, box(targetX - targetMarkerWidth / 2, y - 6, targetMarkerWidth, 12), {
    radius: 2,
  });
  builder.anchor(`${semanticId}.source`, semanticId, point(sourceX, y), directionFor(sourceX, targetX));
  builder.anchor(`${semanticId}.target`, semanticId, point(targetX, y), directionFor(targetX, sourceX));
  builder.anchor(`${semanticId}.label`, semanticId, point(labelCenterX, labelY), 'up');
  builder.hit(semanticId, eventBounds);
  builder.relation(`${semanticId}.delivery`, event.kind, `participant.${event.from}`, `participant.${event.to}`, event.label);

  if (index > 0) {
    const previous = spec.events[index - 1]!;
    builder.relation(`${semanticId}.follows`, 'follows', `event.${previous.id}`, semanticId, `${event.id} occurs after ${previous.id}`);
  }
  if (displayLabel !== event.label) warnings.push(`Event label compacted: ${event.id}`);
}

function participantRole(participant: SequenceParticipant): ShapeRole {
  if (participant.role === 'runtime') return 'slot';
  if (participant.role === 'storage') return 'record';
  if (participant.role === 'service') return 'header';
  return 'surface';
}

function roleForEvent(kind: SequenceEvent['kind']): ShapeRole {
  if (kind === 'call') return 'pointer';
  if (kind === 'return') return 'positive';
  if (kind === 'error') return 'negative';
  return 'capacity';
}

function directionFor(from: number, to: number): 'left' | 'right' {
  return to >= from ? 'right' : 'left';
}

/** Keeps both the verb and distinguishing object visible in narrow layouts. */
function compactLabel(value: string, max: number): string {
  if (value.length <= max) return value;
  const words = value.trim().split(/\s+/);
  if (words.length > 1) {
    const first = words[0]!;
    const last = words.at(-1)!;
    const room = max - first.length - 2;
    if (room >= 2) return `${first}…${last.slice(-room)}`;
  }
  const head = Math.max(2, Math.ceil((max - 1) * 0.58));
  const tail = Math.max(1, max - head - 1);
  return `${value.slice(0, head)}…${value.slice(-tail)}`;
}

/** Participant cards privilege the final, identity-bearing noun on phones. */
function compactParticipantLabel(value: string, max: number): string {
  if (value.length <= max) return value;
  const identity = value.trim().split(/\s+/).at(-1) ?? value;
  if (identity.length <= max) return identity;
  return compactLabel(identity, max);
}

function assertUnique(values: string[], kind: string): void {
  if (new Set(values).size !== values.length) throw new Error(`concurrency-sequence: ${kind} IDs must be unique`);
}

function unique(values: string[]): string[] {
  return [...new Set(values)];
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function n(value: number): number {
  return Math.round(value * 10) / 10;
}
